import { useState, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { useSubscriptionsStore } from '.';
import { useSchedulesStore } from '../schedules';

function useSubscriptions() {
    const subscriptionsStore = useSubscriptionsStore();
    const schedulesStore = useSchedulesStore();

    const [subscriptions, setSubscriptions] = useState([]);
    const [inactiveSubscriptions, setInactiveSubscriptions] = useState([]);
    const [connectedSchedules, setConnectedSchedules] = useState({});
    const [subscriptionDetails, setSubscriptionDetails] = useState({});
    const [availableSchedules, setAvailableSchedules] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [pageInfo, setPageInfo] = useState(subscriptionsStore.pageInfo);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        setSubscriptions(subscriptionsStore.subscriptions);
        setInactiveSubscriptions(subscriptionsStore.inactiveSubscriptions);
        setConnectedSchedules(subscriptionsStore.connectedSchedules);
        setSubscriptionDetails(subscriptionsStore.subscriptionDetails);
        setPageInfo(subscriptionsStore.pageInfo);
    }, [subscriptionsStore.observed.subscriptions]);

    useEffect(() => {
        setAvailableSchedules(subscriptionsStore.availableSchedules);
    }, [subscriptionsStore.observed.availableSchedules]);

    useEffect(() => {
        setIsLoading(subscriptionsStore.loadProgress.subscriptions);
    }, [subscriptionsStore.observed.loadProgress]);

    useEffect(() => {
        setIsLoadingMore(subscriptionsStore.loadingMoreSubscriptions);
    }, [subscriptionsStore.observed.loadingMoreSubscriptions]);

    // ─── Actions — delegates to store, no logic here ────────────────────────

    function loadAvailableSchedules(subscriptionId) {
        if (subscriptionsStore.availableSchedulesLoaded[subscriptionId]) return;
        subscriptionsStore.getAvailableSchedules(subscriptionId);
    }

    function unsubscribe(subscription) {
        return subscriptionsStore.unsubscribe(subscription);
    }

    function addSchedule(subscriptionId, scheduleId) {
        const subscription = subscriptionsStore.subscriptions.find((s) => s.id === subscriptionId);
        const schedule = subscriptionsStore.availableSchedules[subscriptionId]?.find(
            (s) => s.scheduleId === scheduleId || s.id === scheduleId
        );
        if (!subscription || !schedule) return Promise.resolve({ success: false });
        return schedulesStore.addSchedule(subscription, schedule);
    }

    function removeSchedule(subscriptionId, scheduleId) {
        const subscription = subscriptionsStore.subscriptions.find((s) => s.id === subscriptionId);
        const schedule = (subscriptionsStore.connectedSchedules[subscriptionId] || []).find(
            (s) => s.scheduleId === scheduleId || s.id === scheduleId
        );
        if (!subscription || !schedule) return Promise.resolve({ success: false });
        return schedulesStore.deleteSchedule(subscription, schedule);
    }

    function fetchActiveSubscriptions() {
        subscriptionsStore.getActiveSubscriptions();
    }

    function fetchInactiveSubscriptions() {
        subscriptionsStore.getInactiveSubscriptions();
    }

    // Leading-edge so the first click fires immediately; trailing disabled so
    // a rapid burst collapses into that one call instead of queuing another
    // for after the window — the store's own loadingMoreSubscriptions guard
    // still applies on top of this for anything the debounce window misses.
    const loadMoreSubscriptions = useMemo(
        () => debounce(() => subscriptionsStore.nextPage(), 300, { leading: true, trailing: false }),
        [subscriptionsStore]
    );

    useEffect(() => () => loadMoreSubscriptions.cancel(), [loadMoreSubscriptions]);

    return {
        subscriptions,
        inactiveSubscriptions,
        connectedSchedules,
        subscriptionDetails,
        availableSchedules,
        isLoading,
        pageInfo,
        isLoadingMore,
        hasMoreSubscriptions: pageInfo.currentPage < pageInfo.totalPages,
        unsubscribe,
        addSchedule,
        removeSchedule,
        loadAvailableSchedules,
        fetchActiveSubscriptions,
        fetchInactiveSubscriptions,
        loadMoreSubscriptions,
    };
}

export default useSubscriptions;
